import { useState } from 'react'
import { Alert } from './Alert.js'
import { Dialog } from './Dialog.js'
import type { EditableColumn } from './useDeclaredColumns.js'

/**
 * Deleting a column, and saying plainly what that does not do.
 *
 * **The values on contacts stay.** The declaration is what the board offers;
 * what each contact holds is what was imported. Re-declaring the same column
 * brings it all back into view, which is what somebody who deleted the wrong
 * one needs — and stripping the values would mean a mis-click destroys a
 * column of data across every contact in the organisation with nothing to
 * restore from.
 *
 * **A segment that asks about it keeps working**, because the rule reads the
 * values rather than the declaration. It simply stops being offered in the
 * builder. That is the question somebody asks before pressing this, so it is
 * answered before they do rather than afterwards.
 *
 * Only a declared column reaches here. The six the board always has cannot be
 * deleted — the data would still be there, and the menu does not offer it.
 */
export function DeleteColumnDialog({
  column,
  onDelete,
  onClose,
}: {
  readonly column: EditableColumn
  readonly onDelete: () => Promise<string | undefined>
  readonly onClose: () => void
}) {
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [deleting, setDeleting] = useState(false)

  const remove = async () => {
    setDeleting(true)
    setProblem(undefined)
    const refused = await onDelete()
    if (refused === undefined) {
      onClose()
      return
    }
    setProblem(refused)
    setDeleting(false)
  }

  return (
    <Dialog
      footer={
        <>
          <button
            className="rounded-pill bg-danger px-5 py-2.5 font-bold text-[13px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
            disabled={deleting}
            onClick={() => void remove()}
            type="button"
          >
            {deleting ? 'Deleting…' : 'Delete column'}
          </button>
          <button
            className="font-medium text-[13px] text-t3 hover:text-t1"
            onClick={onClose}
            type="button"
          >
            Keep it
          </button>
        </>
      }
      heading={`Delete “${column.label}”?`}
      onClose={onClose}
    >
      {problem === undefined ? null : <Alert>{problem}</Alert>}

      <p className="text-[13px] text-t2">
        The column stops being offered — on the board, in the forms and in the segment builder.
      </p>
      <ul className="mt-3 flex flex-col gap-2 text-[13px] text-t2">
        <li>
          <span className="font-semibold text-t1">What each contact holds is kept.</span> Adding the
          column back under the same key brings every value into view again.
        </li>
        <li>
          <span className="font-semibold text-t1">Segments that ask about it keep working.</span> A
          rule reads the values rather than this declaration, so no audience changes.
        </li>
      </ul>
    </Dialog>
  )
}
